import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import HidePasswordIcon from '../components/HidePasswordIcon';

/**
 * @file
 * @module Pages/Signup
 * @description Signup est une page qui permet à l'utilisateur de créer un nouveau compte.
 * - Elle contient un formulaire où l'utilisateur peut entrer son nom d'utilisateur, son mot de passe, son prénom, son nom de famille, 
 * le nom de son entreprise et son numéro SIRET.
 * - Lorsque le formulaire est soumis, une requête POST est envoyée à l'API pour créer le nouveau compte.
 * - Si la création du compte réussit, l'utilisateur est redirigé vers la page de connexion.
 * - Si la création du compte échoue, un message d'erreur est affiché.
 * - Les informations de l'entreprise et du SIRET peuvent être pré-remplies à partir de l'URL si elles sont fournies sous forme cryptée.
 * - Cette page utilise la bibliothèque CryptoJS pour le chiffrement AES.
 * - Cette page utilise le hook useState de React pour gérer l'état des champs de saisie et de la modale.
 * - Cette page utilise le hook useEffect de React pour décrypter les informations de l'entreprise et du SIRET à partir de l'URL.
 * - Cette page utilise le hook useParams de 'react-router-dom' pour obtenir les informations de l'entreprise et du SIRET à partir de l'URL.
 * - Cette page utilise le composant Modal pour afficher un message de confirmation après la création du compte.
 *
 */

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const Signup = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [company, setCompany] = useState('');
    const [siret, setSiret] = useState('');
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const regexEmail = /^[^@\s]{2,30}@[^\s@]{2,30}\.[^@\s]{2,}$/;
    const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\-+.]).{8,}$/;

    const { encryptedEntreprise, encryptedSiret, signupToken } = useParams();

    useEffect(() => {
        if (signupToken) {
            try {
                const payload = signupToken.split('.')[0];
                const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
                const parsed = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
                const companyName = String(parsed.company || '').trim();
                const siretNumber = String(parsed.siret || '').trim();
                setCompany(companyName);
                setSiret(siretNumber);
                if (!companyName || !siretNumber) {
                    setError("Lien d'inscription invalide.");
                }
            } catch (error) {
                console.error('Lien d\'inscription illisible:', error);
                setError("Lien d'inscription invalide.");
            }
            return;
        }

        if (encryptedEntreprise && encryptedSiret) {
            try {
                const urlSafeEntreprise = decodeURIComponent(encryptedEntreprise);
                const urlSafeSiret = decodeURIComponent(encryptedSiret);

                const bytesEntreprise = CryptoJS.AES.decrypt(urlSafeEntreprise, 'secret key 123');
                const originalEntreprise = bytesEntreprise.toString(CryptoJS.enc.Utf8);
                setCompany(originalEntreprise);
                //console.log('Entreprise décryptée:', originalEntreprise);

                const bytesSiret = CryptoJS.AES.decrypt(urlSafeSiret, 'secret key 123');
                const originalSiret = bytesSiret.toString(CryptoJS.enc.Utf8);
                setSiret(originalSiret);
                //console.log('SIRET décrypté:', originalSiret);

                if (!originalEntreprise?.trim() || !originalSiret?.trim()) {
                    setError("Lien d'inscription invalide : le nom de société et le SIRET sont obligatoires. Utilisez le lien d'invitation fourni.");
                }
            } catch (error) {
                console.error('Erreur de décryptage:', error);
                setError("Lien d'inscription invalide : le nom de société et le SIRET sont obligatoires. Utilisez le lien d'invitation fourni.");
            }
        } else {
            setError("L'inscription n'est possible qu'avec un lien d'invitation contenant le nom de société et le SIRET.");
        }
    }, [encryptedEntreprise, encryptedSiret, signupToken]);

    const hide = () => {
        setShowModal(false);
        navigate('/signin');
    };

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
        if (!regexEmail.test(e.target.value)) {
            setUsernameError("Le nom d'utilisateur doit être une adresse e-mail valide.");
        } else {
            setUsernameError('');
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (!regexPassword.test(e.target.value)) {
            setPasswordError("Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.");
        } else {
            setPasswordError('');
        }
        if (confirmPassword && e.target.value !== confirmPassword) {
            setConfirmPasswordError("Les mots de passe ne correspondent pas.");
        } else {
            setConfirmPasswordError('');
        }
    };

    const sanitizeInput = (input) => {
        const replacements = {
            "'": ' ',  // Remplace les apostrophes par des espaces
            "é": 'e',
            "è": 'e',
            "ê": 'e',
            "à": 'a',
            "ù": 'u',
            "ç": 'c',
        };
    
        return input.replace(/./g, (char) => replacements[char] || char);
    };
    
    

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        if (password && e.target.value !== password) {
            setConfirmPasswordError("Les mots de passe ne correspondent pas.");
        } else {
            setConfirmPasswordError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!regexEmail.test(username)) {
            setError("Le nom d'utilisateur doit être une adresse e-mail valide.");
            return;
        }
        if (!regexPassword.test(password)) {
            setError("Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }
        if (!company.trim() || !siret.trim()) {
            setError("L'inscription n'est possible qu'avec un nom de société et un SIRET. Utilisez le lien d'invitation fourni.");
            return;
        }

        const formData = {
            username: username,
            password: password,
            firstname: sanitizeInput(firstname),
            lastname: sanitizeInput(lastname),
            company: sanitizeInput(company),
            siret: siret,
            ...(signupToken ? { signupToken } : {}),
        };
        // console.log(formData);

        try {
            const response = await fetch(apiPrefix+'register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                console.log('Inscription réussie !');
                setShowModal(true); 
            } else if (response.status === 502) {
                setError("Un compte existe déjà avec cette adresse e-mail.");
            } else if (response.status === 400) {
                const data = await response.json().catch(() => ({}));
                setError(data.error || "Le nom de société et le SIRET sont obligatoires pour l'inscription.");
            } else {
                console.error('Erreur lors de l\'inscription:', response.statusText);
                setError("Erreur lors de l'inscription. Veuillez réessayer.");
            }
        } catch (error) {
            console.error('Erreur lors de la tentative d\'inscription:', error);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center bg-gray-50 p-5 min-h-screen">
            <h2 className="text-gray-800 text-2xl font-bold mb-5">Inscription</h2>
            <form onSubmit={handleSubmit} className="card w-full max-w-md">
                {error && <div className="alert alert-error text-center mb-4">{error}</div>}
                
                <div className="form-group">
                    <label htmlFor="username" className="form-label">
                        <i className="fas fa-envelope mr-2.5 text-primary"></i>
                        Adresse mail :
                    </label>
                    <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={handleUsernameChange}
                        required
                        className="form-input"
                    />
                    {usernameError && <div className="form-error">{usernameError}</div>}
                </div>
                
                <div className="form-group">
                    <label htmlFor="password" className="form-label">
                        <i className="fas fa-key mr-2.5 text-primary"></i>
                        Mot de passe :
                    </label>
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            value={password}
                            onChange={handlePasswordChange}
                            required
                            className="form-input h-10"
                        />
                        <HidePasswordIcon showPassword={showPassword} setShowPassword={setShowPassword} />
                    </div>
                    {passwordError && <div className="form-error">{passwordError}</div>}
                </div>
                
                <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                        <i className="fas fa-key mr-2.5 text-primary"></i>
                        Confirmer le mot de passe :
                    </label>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                        className="form-input h-10"
                    />
                    {confirmPasswordError && <div className="form-error">{confirmPasswordError}</div>}
                </div>
                
                <div className="form-group">
                    <label htmlFor="firstname" className="form-label">
                        <i className="fas fa-user mr-2.5 text-primary"></i>
                        Prénom :
                    </label>
                    <input
                        type="text"
                        id="firstname"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        required
                        className="form-input"
                    />
                </div>
                
                <div className="form-group">
                    <label htmlFor="lastname" className="form-label">
                        <i className="fas fa-user mr-2.5 text-primary"></i>
                        Nom de famille :
                    </label>
                    <input
                        type="text"
                        id="lastname"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        required
                        className="form-input"
                    />
                </div>
                
                <div className="form-group">
                    <input
                        type="text"
                        id="company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        disabled
                        hidden
                    />
                </div>
                <div className="form-group">
                    <input
                        type="text"
                        id="siret"
                        value={siret}
                        onChange={(e) => setSiret(e.target.value)}
                        disabled
                        hidden
                    />
                </div>
                
                <button
                    type="submit"
                    className="btn-primary w-full mt-5"
                    disabled={!company.trim() || !siret.trim()}
                >
                    S'inscrire
                </button>
            </form>
            {showModal && (
                <div className="modal-overlay" onClick={hide}>
                    <div className="modal-content">
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={hide}
                        >
                            ×
                        </button>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-primary mb-4">Succès !</h2>
                            <p className="text-gray-700 mb-6">Inscription réussie !</p>
                            <button 
                                onClick={hide}
                                className="btn-primary"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Signup;